import type { SerializedStyles } from "@emotion/serialize";
import { Checkbox } from "@fullscript/aviary-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, ShowMoreButton, Spacer } from "@aviary";
import { useFormsV2SharedQuery } from "@shared/data/queries/FormsV2Query/FormsV2.query.generated";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { l } from "@shared/locales/i18n";
import type * as Types from "@shared/types/graphqlGenerated";

import type { ContextualFilter, Filter } from "../../CatalogSearch";
import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./Form.styles";

type Form = Pick<Types.DosageFormatContextualFilters, "id" | "esResultCount"> & {
  dosageFormat: Pick<Types.DosageFormat, "id" | "baseFormat" | "variation">;
};
type NonContextualForm = Pick<Types.BaseFormat, "id" | "baseFormatName">;

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  title?: string;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
  isContextual?: boolean;
  contextualData?: Form[];
}

const Form = ({
  isBold,
  logFilterEvent,
  title,
  customAccordionStyle,
  isContextual = false,
  contextualData,
}: Props) => {
  const { t } = useTranslation();
  const [isPillPacksEnabled] = useFlippers("cura_daily_packs_phase_2");
  const { activeFilters, setActiveFilters } = useCatalogSearch();
  const { data, loading } = useFormsV2SharedQuery({
    skip: isContextual,
  });

  const [showAll, setShowAll] = useState(false);

  const showLessCutoff = 5;

  // new contextual filters that include result count
  const formsData: Form[] = contextualData ?? [];

  // forms have duplicated baseFormats because they might be different variations in the same baseFormat
  const formFilterOptions: ContextualFilter[] =
    formsData
      ?.filter((formWithCount: Form) => {
        // Only allow Pill Packs filter to show if the flipper is on
        return isPillPacksEnabled
          ? formWithCount.dosageFormat.baseFormat
          : formWithCount.dosageFormat.baseFormat !== "Daily Packs";
      })
      ?.filter((formWithCount: Form) => (formWithCount?.esResultCount ?? 0) > 0)
      ?.reduce<ContextualFilter[]>((unique, formWithCount) => {
        if (
          unique.find(uniqueElement => uniqueElement.name === formWithCount.dosageFormat.baseFormat)
        ) {
          return unique;
        }

        // Element is not there already
        return [
          ...unique,
          {
            id: formWithCount.dosageFormat.id,
            name: formWithCount.dosageFormat.baseFormat,
            category: "Form" as const,
            esResultCount: formWithCount.esResultCount ?? 0,
          },
        ];
      }, []) ?? [];

  const renderForms = () =>
    formFilterOptions
      .sort((a: ContextualFilter, b: ContextualFilter) => {
        return (b?.esResultCount ?? 0) - (a?.esResultCount ?? 0);
      })
      .map((form, index) => {
        if (showAll || index < showLessCutoff) {
          return (
            <span key={form.id} css={styles.checkbox}>
              <Checkbox
                isFullWidth
                checked={isFormActive(form.id)}
                onChange={() => handleFormSelect(form)}
              >
                {`${form.name} (${form.esResultCount})`}
              </Checkbox>
            </span>
          );
        }

        return null;
      });

  const handleFormSelect = (formSelected: ContextualFilter) => {
    const distanceFromTop = window?.scrollY;

    const selectedForm: Filter = {
      name: formSelected.name,
      id: formSelected.id,
      category: formSelected.category,
    };

    if (isFormActive(selectedForm.id)) {
      setActiveFilters(activeFilters.filter(form => form.name !== selectedForm.name));
    } else {
      logFilterEvent(selectedForm.name, FilterType.FORM);
      setActiveFilters([...activeFilters, selectedForm]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderShowMoreButton = () => {
    if ((formFilterOptions?.length ?? 0) <= showLessCutoff) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  // old filters that don't include result count
  const nonContextualFormsData: NonContextualForm[] = data?.viewer?.formsV2 ?? [];

  // forms have duplicated baseFormats because they might be different variations in the same baseFormat
  const nonContextualFormFilterOptions: NonContextualForm[] =
    nonContextualFormsData?.filter((form: NonContextualForm) => {
      // Only allow Pill Packs filter to show if the flipper is on
      return isPillPacksEnabled ? form : form.baseFormatName !== "Daily Packs";
    }) ?? [];

  const renderNonContextualForms = () =>
    nonContextualFormFilterOptions.map((form: NonContextualForm, index: number) => {
      if (showAll || index < showLessCutoff) {
        return (
          <span key={form.id} css={styles.checkbox}>
            <Checkbox
              isFullWidth
              checked={isFormActive(form.id)}
              onChange={() =>
                handleNonContextualFormSelect({
                  name: form.baseFormatName,
                  id: form.id,
                  category: "Form",
                })
              }
            >
              {form.baseFormatName}
            </Checkbox>
          </span>
        );
      }

      return null;
    });

  const handleNonContextualFormSelect = (formSelected: Filter) => {
    const distanceFromTop = window?.scrollY;

    const selectedForm: Filter = formSelected;

    if (isFormActive(selectedForm.id)) {
      setActiveFilters(activeFilters.filter(form => form.name !== selectedForm.name));
    } else {
      logFilterEvent(selectedForm.name, FilterType.FORM);
      setActiveFilters([...activeFilters, selectedForm]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderNonContextualShowMoreButton = () => {
    if ((nonContextualFormFilterOptions?.length ?? 0) <= showLessCutoff) {
      return null;
    }

    return (
      <>
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </>
    );
  };

  // shared logic for both contextual and non-contextual filters
  if (isContextual ? (contextualData?.length ?? 0) === 0 : loading || !data?.viewer?.formsV2) {
    return null;
  }

  const isFormActive = (id: string): boolean =>
    activeFilters.some(activeForm => activeForm.id === id);

  const activeFormCount = activeFilters.filter(active => active?.category === "Form")?.length;

  return (
    <Accordion
      css={customAccordionStyle}
      coreTriggerContent={
        <FilterTitle
          title={title || t(l.sharedCatalog.filter.Form)}
          count={activeFormCount}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      <div css={styles.checkboxList}>
        {isContextual ? renderForms() : renderNonContextualForms()}
        {isContextual ? renderShowMoreButton() : renderNonContextualShowMoreButton()}
      </div>
    </Accordion>
  );
};

export { Form };
